import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, Subscription, throwError } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { IProvider, IProviderSearch } from 'src/app/core/models/state.model';
import { ProviderService } from 'src/app/core/services/provider.service';
import * as polCoreActions from 'src/app/core/state/core.actions';
import { getProviderState } from 'src/app/core/state/core.selectors';
import { takeUntil } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { AppModule } from 'src/app/app.module';

@Component({
  selector: 'app-provider-search',
  templateUrl: './provider-search.component.html',
  styleUrls: ['./provider-search.component.scss']
})
export class ProviderSearchComponent implements OnInit, OnDestroy {
  ngUnsubscribe = new Subject<void>();
  private subscriptions = new Subscription();

  @Input() props: any;
  @Input() displayClearResults: boolean = false;
  @Input() isMaxProviderLengthWarning: boolean = false;
  @Input() isSubTypeNotFacilityOrPractitioner: boolean = false;
  @Input() maxProviderBookmarkCount: number;
  providereSearchTerm$ = new Subject<string>();
  loading = false;

  favoriteProviders = [];
  providerResults: IProvider[] = [];
  suggestiveResponseHasMore: any; 
  searchResult = [];
  errorAddingFavourites: string;
  selectedProvider: IProvider;

  favoriteProviders$ = this.store.select(getProviderState).pipe(takeUntil(this.ngUnsubscribe));
  hasSearchValue: string;
  showNoResultsMsg: boolean = false;
  searchValue: string = '';
  lengthErrorMessage: string = '';

  constructor(
    private store: Store,
    private providerService: ProviderService,
    public toastr: ToastrService
  ) {}

  ngOnInit() {
    this.handleProviderSearch();
    this.subscriptions.add(
      this.favoriteProviders$.subscribe((res) => {
        if (!res.loading) {
          this.selectedProvider = res.selectedProvider;
          this.favoriteProviders = res.favoriteProviders;
          if (res.clearProviderSearchResults) {
            this.providerResults = [];
            this.showNoResultsMsg = false;
            this.store.dispatch(polCoreActions.resetProviderSearchResults());
          }
        }
      })
    );
  }

  validateInput() {
    if(this.searchValue && this.searchValue.length < 2) {
      this.lengthErrorMessage = "Provider Search must be a minimum of 2 characters";
    } else {
      this.lengthErrorMessage = null;
    }
  }

  handleProviderSearch() {
    this.providereSearchTerm$
      ?.pipe(
        debounceTime(500),
        distinctUntilChanged(),
        filter((q) => !!q && q.length > 1),
        switchMap((value: string) => {
          this.loading = true;
          this.hasSearchValue = value;
          this.showNoResultsMsg = false;
          return this.providerService.searchProvider(value).pipe(
            catchError((error: HttpErrorResponse) => {
              console.error('An error occurred:', error.error);
              return throwError('Something bad happened; please try again later.');
            })
          );
        })
      )
      .subscribe(
        (data: any) => {
          this.showNoResultsMsg = this.hasSearchValue && !data?.results?.length;
          this.loading = false;
          this.providerResults = data.results.map((provider: IProviderSearch) => ({
            name: `${provider.providerNPI ? provider.providerNPI + ' - ' : ''}ID ${provider.sourceProviderNumber} - ${
              provider.providerFullName
            }`,
            value: `${provider.providerNPI ? provider.providerNPI + ' - ' : ''}ID ${provider.sourceProviderNumber} - ${
              provider.providerFullName
            }`,
            providerId: provider.sourceProviderNumber,
            npi: provider.providerNPI,
            fullName: provider.providerFullName,
            address: provider.address,
            subTypeDesc: provider.subType
          }));

          // Get has more value for suggestive endpoint
          this.suggestiveResponseHasMore = data.hasMore;
          
          // implement toast if hasMore is true
          if (this.suggestiveResponseHasMore) {
            const toastrTitle = 'Search Results';
            const toastrMessage = 'The search returned more than 50 results. If the provider you are searching for does not appear in the results, please refine your search and try again.';
            const toast = this.toastr.info(toastrMessage, toastrTitle, {
              disableTimeOut: false,
              closeButton: true,
              tapToDismiss: true,
              progressBar: true,
              timeOut: 10000,
              extendedTimeOut: 10000
            });
            AppModule.toasts.push(toast);
          }          
        },
        (error) => {
          console.log(error);
          this.providerResults = [];
          this.loading = false;
        }
      );
  }

  searchProviders(event) {
    this.searchValue = event;
    if (event === '') {
      this.showNoResultsMsg = false;
    }
    this.providereSearchTerm$.next(event);
  }

  selectProviders(selectedProvider: string) {
    this.errorAddingFavourites = '';

    const searchResult = this.providerResults.filter((provider) => provider.value === selectedProvider);
    this.store.dispatch(polCoreActions.setSelectedProvider({ selectedProvider: searchResult[0] }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
